import { redirectToLoginIfRequired } from '../utils/common';
import Logger from '../utils/logger';
import { getMarketSdk, getUserSdk } from '../utils/sdk';
import types from './_actionTypes';

function validateUser(user) {
  if (!user || !user.id || user.tokenIsValid !== true || !user.token) {
    Logger.error({
      errorCode: 'E0022',
      message: 'Invalid Token'
    });
    return false;
  }
  return true;
}

function initializeMarketSdk(user) {
  if (!validateUser(user)) {
    return null;
  }
  return getMarketSdk(user.token);
}

function initializeUserSdk(user) {
  if (!validateUser(user)) {
    return null;
  }
  return getUserSdk(user.token);
}

function receiveScanResponse(scanName, response) {
  return {
    type: types.RECEIVE_SCAN,
    name: scanName,
    data: response
  };
}

function receiveUserScansResponse(response) {
  return {
    type: types.RECEIVE_USER_SCANS,
    data: response
  };
}

function receiveLastScanResponse(response) {
  return {
    type: types.RECEIVE_LAST_SCAN,
    data: response
  };
}

function saveLastScanResponse(scan) {
  return {
    type: types.SAVE_LAST_SCAN,
    data: scan
  };
}

function saveScanResponse(scan) {
  return {
    type: types.SAVE_SCAN,
    data: scan
  };
}

function overwriteScanResponse(scan) {
  return {
    type: types.OVERWRITE_SCAN,
    data: scan
  };
}

function deleteScanResponse(response, scanId) {
  return {
    type: types.DELETE_SCAN,
    id: scanId,
    data: response
  };
}

export function errorRequestingScan(scanName, error) {
  return {
    type: types.ERROR_REQUESTING_SCAN,
    name: scanName,
    error: error,
    errorCode: 'E0018'
  };
}

export function errorRequestingUserScans(error) {
  return {
    type: types.ERROR_REQUESTING_USER_SCANS,
    error: error,
    errorCode: 'E0032'
  };
}

export function errorRequestingLastScan(error) {
  return {
    type: types.ERROR_REQUESTING_LAST_SCAN,
    error: error,
    errorCode: 'E0033'
  };
}

export function errorSavingLastScan(lastScan, error) {
  return {
    type: types.ERROR_SAVING_LAST_SCAN,
    lastScan: lastScan,
    error: error,
    errorCode: 'E0034'
  };
}

export function errorSavingScan(scan, error) {
  return {
    type: types.ERROR_SAVING_SCAN,
    scan: scan,
    error: error,
    errorCode: 'E0035'
  };
}

export function errorOverwritingScan(scan, error) {
  return {
    type: types.ERROR_OVERWRITING_SCAN,
    scan: scan,
    error: error,
    errorCode: 'E0036'
  };
}

export function errorDeletingScan(scanId, error) {
  return {
    type: types.ERROR_DELETING_SCAN,
    scanId: scanId,
    error: error,
    errorCode: 'E0037'
  };
}

export function errorRenamingScan(scan, error) {
  return {
    type: types.ERROR_RENAMING_SCAN,
    scan: scan,
    error: error,
    errorCode: 'E0042'
  };
}

export function setSelectedScan(scan) {
  return {
    type: types.SET_SELECTED_SCAN,
    data: scan
  };
}

export function setScanCriteriaEnabled(fieldId, enabled) {
  return {
    type: types.SET_ENABLED_SCAN_CRITERIA,
    fieldId,
    enabled
  };
}

export function resetScanCriteriaEnabled() {
  return {
    type: types.RESET_ENABLED_SCAN_CRITERIA
  };
}

/**
 * Fetches scan results using the insight-market-sdk
 * @param {string} scanName - The name of the scan to fetch
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(fetchScan('SecurityType'))
 */
export function fetchScan(scanName) {
  return (dispatch, getState) => {
    const sdk = initializeMarketSdk(getState().user);
    if (!sdk) {
      dispatch(
        errorRequestingScan(
          scanName,
          new Error('SDK initialization failed - invalid or missing token')
        )
      );
      return false;
    }

    return sdk.scans
      .getScanResults(scanName)
      .then((response) => {
        // Validate SDK response structure
        if (!response) {
          throw new Error(`SDK returned null/undefined for scan: ${scanName}`);
        }

        // The SDK response is directly an object of symbol names/IDs
        // Structure: { [symbolId]: { filterValue: string, displayValues: {...} } }
        if (typeof response !== 'object' || Array.isArray(response)) {
          throw new Error(
            `SDK response must be an object with symbol IDs as keys. Received: ${JSON.stringify(
              response
            )}`
          );
        }

        // Transform to expected format: { data: { symbols: response }, ok: true }
        // The response itself is the symbols object
        const transformedResponse = {
          data: {
            symbols: response
          },
          ok: true
        };

        return dispatch(receiveScanResponse(scanName, transformedResponse));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorRequestingScan(scanName, ex));
      });
  };
}

/**
 * Fetches user scans using the insight-user-sdk
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(fetchUserScans())
 */
export function fetchUserScans() {
  return (dispatch, getState) => {
    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      dispatch(
        errorRequestingUserScans(
          new Error('SDK initialization failed - invalid or missing token')
        )
      );
      return false;
    }

    return sdk.scans
      .getAll()
      .then((response) => {
        // Validate SDK response
        if (!response) {
          throw new Error('SDK returned null/undefined for user scans');
        }

        // Transform SDK response to match expected format
        // SDK returns: { scans: Scan[], staticScans: Scan[] }
        // Expected: { data: { scans: Scan[], staticScans: Scan[] } }
        const transformedResponse = {
          data: {
            scans: response.scans || [],
            staticScans: response.staticScans || []
          }
        };

        return dispatch(receiveUserScansResponse(transformedResponse));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorRequestingUserScans(ex));
      });
  };
}

/**
 * Fetches the last scan using the insight-user-sdk
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(fetchLastScan())
 */
export function fetchLastScan() {
  return (dispatch, getState) => {
    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      dispatch(
        errorRequestingLastScan(
          new Error('SDK initialization failed - invalid or missing token')
        )
      );
      return false;
    }

    return sdk.scans
      .getById('last-scan')
      .then((scan) => {
        // Validate SDK response
        if (!scan) {
          throw new Error('SDK returned null/undefined for last scan');
        }

        // Transform SDK response to match expected format
        // SDK returns: Scan object
        // Expected: { data: { scan: Scan } }
        const transformedResponse = {
          data: {
            scan: scan
          }
        };

        return dispatch(receiveLastScanResponse(transformedResponse));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorRequestingLastScan(ex));
      });
  };
}

/**
 * Saves the last scan using the insight-user-sdk
 * @param {Object} lastScan - The last scan object to save
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(saveLastScan(scanObject))
 */
export function saveLastScan(lastScan) {
  return (dispatch, getState) => {
    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      const error = new Error(
        'SDK initialization failed - invalid or missing token'
      );
      dispatch(errorSavingLastScan(lastScan, error));
      return false;
    }

    return sdk.scans
      .replaceById('last-scan', lastScan)
      .then(() => {
        // SDK returns void on success
        // Transform to expected format for reducer
        const transformedResponse = lastScan;

        return dispatch(saveLastScanResponse(transformedResponse));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorSavingLastScan(lastScan, ex));
      });
  };
}

/**
 * Creates a new user scan using the insight-user-sdk
 * @param {Object} newScan - The scan object to create
 * @param {Function} handleSuccess - Success callback receiving the scan ID
 * @param {Function} handleFail - Failure callback
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(saveScan(scanObject, successCallback, failCallback))
 */
export function saveScan(newScan, handleSuccess, handleFail) {
  return (dispatch, getState) => {
    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      const error = new Error(
        'SDK initialization failed - invalid or missing token'
      );
      dispatch(errorSavingScan(newScan, error));
      if (handleFail) handleFail(error);
      return false;
    }

    return sdk.scans
      .add(newScan)
      .then((createdScan) => {
        // Validate SDK response
        if (!createdScan || !createdScan.id) {
          throw new Error('SDK returned invalid scan response');
        }

        // Transform to expected format: { data: { scan: createdScan } }
        const transformedResponse = {
          data: {
            scan: createdScan
          }
        };

        // Call success handler with the generated scan ID
        if (handleSuccess) {
          handleSuccess(createdScan.id);
        }

        return dispatch(saveScanResponse(transformedResponse));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorSavingScan(newScan, ex));
        if (handleFail) handleFail(ex);
      });
  };
}

/**
 * Updates an existing user scan using the insight-user-sdk
 * @param {Object} existingScan - The scan object to update (must include id)
 * @param {Function} handleSuccess - Success callback
 * @param {Function} handleFail - Failure callback
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(overwriteScan(scanObject, successCallback, failCallback))
 */
export function overwriteScan(existingScan, handleSuccess, handleFail) {
  return (dispatch, getState) => {
    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      const error = new Error(
        'SDK initialization failed - invalid or missing token'
      );
      dispatch(errorOverwritingScan(existingScan, error));
      if (handleFail) handleFail(error);
      return false;
    }

    if (!existingScan.id) {
      const error = new Error('Scan ID is required for update operation');
      dispatch(errorOverwritingScan(existingScan, error));
      if (handleFail) handleFail(error);
      return false;
    }

    return sdk.scans
      .replaceById(existingScan.id, existingScan)
      .then(() => {
        // SDK returns void on success
        // Reducer doesn't use this data, so pass null
        if (handleSuccess) {
          handleSuccess();
        }

        return dispatch(overwriteScanResponse(null));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorOverwritingScan(existingScan, ex));
        if (handleFail) handleFail(ex);
      });
  };
}

/**
 * Deletes a user scan using the insight-user-sdk
 * @param {string} scanId - The ID of the scan to delete
 * @param {Function} handleSuccess - Success callback
 * @param {Function} handleFail - Failure callback
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(deleteScan('scanId', successCallback, failCallback))
 */
export function deleteScan(scanId, handleSuccess, handleFail) {
  return (dispatch, getState) => {
    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      const error = new Error(
        'SDK initialization failed - invalid or missing token'
      );
      dispatch(errorDeletingScan(scanId, error));
      if (handleFail) handleFail(error);
      return false;
    }

    if (!scanId) {
      const error = new Error('Scan ID is required for delete operation');
      dispatch(errorDeletingScan(scanId, error));
      if (handleFail) handleFail(error);
      return false;
    }

    return sdk.scans
      .deleteById(scanId)
      .then(() => {
        // SDK returns void on success
        // Reducer only uses scanId, so pass null for data
        if (handleSuccess) {
          handleSuccess();
        }

        return dispatch(deleteScanResponse(null, scanId));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorDeletingScan(scanId, ex));
        if (handleFail) handleFail(ex);
      });
  };
}

/**
 * Renames an existing user scan by updating the name field.
 * Performs an optimistic update in the Redux store, rolling back on failure.
 * @param {Object} scan - The existing scan object (must include id, name, fields)
 * @param {string} newName - The new name for the scan
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(renameScan(scan, 'New Scan Name'))
 */
export function renameScan(scan, newName) {
  return (dispatch, getState) => {
    if (!scan.id) {
      const error = new Error('Scan ID is required for rename operation');
      dispatch(errorRenamingScan(scan, error));
      return false;
    }

    const renamedScan = { ...scan, name: newName };

    // Optimistic update
    dispatch({ type: types.RENAME_SCAN, data: renamedScan });

    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      const error = new Error(
        'SDK initialization failed - invalid or missing token'
      );
      // Rollback
      dispatch({ type: types.RENAME_SCAN, data: scan });
      dispatch(errorRenamingScan(scan, error));
      return false;
    }

    return sdk.scans
      .replaceById(renamedScan.id, renamedScan)
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        // Rollback
        dispatch({ type: types.RENAME_SCAN, data: scan });
        dispatch(errorRenamingScan(scan, ex));
      });
  };
}
